import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import React, { useRef } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FormEditor from "@/components/features/forms/form-editor";
import { CreateFormMutationData, FormEditorRef } from "@/components/features/forms/form-editor/types";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { toast } from "sonner";
import { TRPCClientError } from "@trpc/client";
import { saveFormResponseToCreateForm } from "@/components/features/forms/form-editor/helpers";
import * as Icons from "lucide-react"
import { TReportType } from "@/types/trpc.types";
import MultipleSelector from "@/components/ui/multi-select";

interface Props {
    trigger?: React.ReactNode
    onCreated?: () => void
}

const formSchema = z.object({
    name: z.string().min(1, "Name is required"),
    type: z.string().min(1, "Type is required").array(),
})

const CreateReportTemplate = ({ trigger, onCreated }: Props) => {
    const { trpc } = useTRPC()
    const [open, setOpen] = React.useState(false);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
    });

    const formRef = useRef<FormEditorRef>(null);

    interface CreateReportMutationData {
        name: string;
        type: TReportType[];
        form: CreateFormMutationData;
    }

    const createReportMutation = useMutation({
        mutationFn: async (data:CreateReportMutationData) => {
            await trpc.reports.templates.create.mutate(data)
        },
        onSuccess: () => {
            setOpen(false)
            toast.success("Sjabloon succesvol aangemaakt")
            onCreated?.()
        },
        onError: (err) => {
            if(err instanceof TRPCClientError) {
                toast.error(err.message)
            } else if(err instanceof Error) {
                toast.error(err.message)
            } else {
                toast.error("Er is een onbekende fout opgetreden")
            }
        }
    })

    const handleSubmit = () => {
        if(!form.formState.isValid) return;
        if(!formRef.current) return;

        const values = form.getValues();
        const formValues = formRef.current.saveForm();
        const formattedForm = saveFormResponseToCreateForm(formValues, {});
        
        createReportMutation.mutate({
            name: values.name,
            type: values.type as TReportType[],
            form: formattedForm
        })
    }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
            {trigger}
        </SheetTrigger>
        <SheetContent className="md:max-w-7xl">
            <SheetHeader>
                <SheetTitle>Nieuw sjabloon</SheetTitle>
                <SheetDescription>Vul de velden in om een nieuw sjabloon te maken</SheetDescription>
            </SheetHeader>
            <div className="h-full overflow-auto px-4">
                <div className="flex flex-col justify-between h-full">
                    <div className="flex flex-col gap-4 h-full">
                        <div className="space-y-1">
                            <Label>Naam</Label>
                            <Input {...form.register("name")} />
                        </div>
                        <div className="space-y-1">
                            <Label>Type rapport</Label>
                            <Controller 
                                control={form.control}
                                name="type"
                                render={({ field }) => (
                                    <MultipleSelector 
                                        selectedOptions={field.value} 
                                        onValueChange={(value)=>field.onChange(value)}
                                        options={[
                                            { value: "screening", label: "Screening" },
                                            { value: "scouting", label: "Scouting" },
                                            { value: "data", label: "Data" },
                                            { value: "check", label: "Check" },
                                            { value: "other", label: "Andere" },
                                        ]}
                                    />
                                )}
                            />                            
                        </div>

                        <div className="overflow-auto h-full ">
                            <FormEditor 
                                ref={formRef}
                            />
                        </div>
                    </div>
                </div>
                
            </div>
            <SheetFooter>
                <div className="flex items-center justify-end gap-2">
                    <Button onClick={()=>{setOpen(false)}} variant='outline'>Sluiten</Button>
                    <Button onClick={()=>{handleSubmit()}} disabled={!form.formState.isValid || createReportMutation.isLoading}>
                        {createReportMutation.isLoading && <Icons.Loader2 className="w-4 h-4 animate-spin" />}
                        Opslaan
                    </Button>
                </div>
            </SheetFooter>
        </SheetContent>
    </Sheet>
  )
}

export default CreateReportTemplate